import React, { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { ShieldCheck } from "lucide-react";
import { GoogleLogin } from "@react-oauth/google";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import PasswordStrength from "@/components/PasswordStrength";
import { api, formatApiError } from "@/lib/api";
import { AUTH } from "@/constants/testIds";
import { toast } from "sonner";

export default function Signup() {
  const { register, googleLogin } = useAuth();
  const nav = useNavigate();
  const [sp] = useSearchParams();
  const referral = sp.get("ref") || "";
  const initial = sp.get("type") === "b2b" ? "b2b" : "b2c";
  const [tab, setTab] = useState(initial);
  const [form, setForm] = useState({ name: "", email: "", password: "", phone: "", address: "", org_name: "" });
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState(null);
  const [refPreview, setRefPreview] = useState(null);   // {valid, referrer_name, discount_message}

  React.useEffect(() => {
    if (!referral) { setRefPreview(null); return; }
    api.get(`/referrers/validate/${encodeURIComponent(referral)}`)
      .then((r) => setRefPreview(r.data))
      .catch(() => setRefPreview(null));
  }, [referral]);

  const update = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setErr(null);
    if (form.password.length < 8) return setErr("Password must be at least 8 characters.");
    setLoading(true);
    try {
      const payload = {
        name: form.name,
        email: form.email,
        password: form.password,
        phone: form.phone,
        address: form.address,
        role: tab === "b2b" ? "b2b_admin" : "customer",
        org_name: tab === "b2b" ? form.org_name : undefined,
      };
      // Referral code is passed via header so backend links accounts
      const config = referral ? { headers: { "X-Referral-Code": referral } } : undefined;
      const { data } = await api.post("/auth/register", payload, config);
      // Update context by refetching /me (register endpoint sets cookies)
      toast.success(referral ? `Account created — thanks for joining via a referral!` : "Account created. Welcome!");
      window.location.href = "/app";
    } catch (e) {
      setErr(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setLoading(false);
    }
  };

  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  const onGoogleSuccess = async (resp) => {
    try {
      await googleLogin({ credential: resp.credential });
      toast.success("Signed up with Google");
      window.location.href = "/app";
    } catch (e) {
      setErr(formatApiError(e.response?.data?.detail) || e.message);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-background grain px-4 py-10">
      <div className="w-full max-w-lg">
        <Link to="/" className="flex items-center gap-2 mb-8">
          <div className="w-10 h-10 rounded-xl bg-primary flex items-center justify-center"><ShieldCheck className="w-5 h-5 text-secondary" /></div>
          <span className="font-display font-semibold text-lg">Treasurescare</span>
        </Link>
        <div className="overline text-secondary">Create your account</div>
        <h1 className="mt-2 font-display text-4xl font-light tracking-tight">A minute to set up. Peace of mind for years.</h1>
        {referral && (
          <div className="mt-4 inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-secondary/15 text-secondary text-xs" data-testid="signup-referral-badge">
            <span className="font-semibold">Referral applied:</span> {referral}
            {refPreview?.valid && refPreview.referrer_name && (
              <span className="ml-1">· from <strong>{refPreview.referrer_name}</strong></span>
            )}
          </div>
        )}
        {referral && refPreview?.valid && refPreview.discount_message && (
          <div className="mt-2 text-xs text-primary" data-testid="signup-referral-discount">
            {refPreview.discount_message}
          </div>
        )}

        <Tabs value={tab} onValueChange={setTab} className="mt-8">
          <TabsList className="grid grid-cols-2 w-full">
            <TabsTrigger value="b2c" data-testid={AUTH.signupTabB2C}>Individual</TabsTrigger>
            <TabsTrigger value="b2b" data-testid={AUTH.signupTabB2B}>Business</TabsTrigger>
          </TabsList>
          <TabsContent value="b2c" className="pt-6">
            <form onSubmit={submit} className="space-y-4">
              <div>
                <Label className="text-xs">Full name</Label>
                <Input required data-testid={AUTH.signupName} value={form.name} onChange={update("name")} className="mt-1.5" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs">Email</Label>
                  <Input required type="email" data-testid={AUTH.signupEmail} value={form.email} onChange={update("email")} className="mt-1.5" />
                </div>
                <div>
                  <Label className="text-xs">Password</Label>
                  <Input required type="password" data-testid={AUTH.signupPassword} value={form.password} onChange={update("password")} className="mt-1.5" placeholder="At least 8 chars" />
                  <PasswordStrength password={form.password} className="mt-2" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs">Mobile</Label>
                  <Input value={form.phone} onChange={update("phone")} className="mt-1.5" placeholder="+44…" />
                </div>
                <div>
                  <Label className="text-xs">Address</Label>
                  <Input value={form.address} onChange={update("address")} className="mt-1.5" placeholder="Street, City" />
                </div>
              </div>
              {err && <div className="text-sm text-destructive">{err}</div>}
              <Button type="submit" data-testid={AUTH.signupSubmit} disabled={loading} className="w-full bg-primary text-primary-foreground">
                {loading ? "Creating…" : "Create account"}
              </Button>
            </form>
          </TabsContent>
          <TabsContent value="b2b" className="pt-6">
            <form onSubmit={submit} className="space-y-4">
              <div>
                <Label className="text-xs">Organisation name</Label>
                <Input required data-testid={AUTH.signupOrgName} value={form.org_name} onChange={update("org_name")} className="mt-1.5" placeholder="ACME Ltd." />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs">Admin name</Label>
                  <Input required data-testid={AUTH.signupName} value={form.name} onChange={update("name")} className="mt-1.5" />
                </div>
                <div>
                  <Label className="text-xs">Work email</Label>
                  <Input required type="email" data-testid={AUTH.signupEmail} value={form.email} onChange={update("email")} className="mt-1.5" />
                </div>
              </div>
              <div>
                <Label className="text-xs">Password</Label>
                <Input required type="password" data-testid={AUTH.signupPassword} value={form.password} onChange={update("password")} className="mt-1.5" placeholder="At least 8 chars" />
                <PasswordStrength password={form.password} className="mt-2" />
              </div>
              {err && <div className="text-sm text-destructive">{err}</div>}
              <Button type="submit" data-testid={AUTH.signupSubmit} disabled={loading} className="w-full bg-primary text-primary-foreground">
                {loading ? "Creating…" : "Create business account"}
              </Button>
            </form>
          </TabsContent>
        </Tabs>

        <div className="relative py-4 mt-6">
          <div className="absolute inset-0 flex items-center"><div className="w-full border-t border-border" /></div>
          <div className="relative flex justify-center text-[10px] overline text-muted-foreground bg-background px-3">or continue with</div>
        </div>
        <div className="flex justify-center" data-testid="signup-google-btn">
          <GoogleLogin
            onSuccess={onGoogleSuccess}
            onError={() => setErr("Google sign-in couldn't load. If you see 'origin_mismatch', add this URL to Google Cloud Console → Credentials → Authorized JavaScript origins. Meanwhile, register with email + password.")}
            theme="outline"
            size="large"
            text="signup_with"
            shape="rectangular"
            logo_alignment="left"
            width="360"
          />
        </div>

        <div className="mt-6 text-sm text-muted-foreground">
          Already have an account? <Link to="/login" className="text-primary font-medium hover:underline">Sign in</Link>
        </div>
      </div>
    </div>
  );
}
